<template>
    <section>
        <ds-modal
            :is-open="isTaskModalOpen"
            class="regular-modal"
            show-close
            :sub-title="fullName"
            :size="modalSize"
            :title.sync="modalTitle"
            @close="modalClose"
        >
            <template #header>
                <ds-text-button
                    v-if="showAction"
                    :loading="saving"
                    :disabled="actionDisabled"
                    @click="modalAction"
                >
                    {{ actionText }}
                </ds-text-button>
            </template>

            <template #default>
                <div class="layout">
                    <div>
                        <ds-input-field
                            v-model="task.title"
                            class="input-border"
                            name="Title"
                            label="Title"
                            required
                            :submitted="submitted"
                        />
                        <div class="date-fields">
                            <ds-date-picker
                                v-model="task.dueDate"
                                class="date-field"
                                name="dueDate"
                                label="Due date"
                            />

                            <span class="date-space">at</span>

                            <time-input
                                v-model="dueTime"
                                name="time-input"
                                class="date-field"
                                :submitted="submitted"
                                required
                                placeholder="Time"
                            />
                        </div>

                        <ds-select-field
                            v-model="task.remindTimeOffsetInMillis"
                            :options="reminderOptionsData"
                            bind-value-only
                            class="input-border"
                            name="reminder"
                            label="Reminder"
                        />
                    </div>

                    <div class="moreOptions">
                        <ds-text-button
                            dense
                            :trailing-icon="!moreOptions ? 'chevron-down' : 'chevron-up'"
                            @click="toggleMoreOptions"
                        >
                            More options
                        </ds-text-button>
                    </div>

                    <div v-if="moreOptions">
                        <ds-text-area-field
                            v-model="task.notes"
                            type="text"
                            name="notes"
                            label="Notes"
                            data-qa="task-notes"
                        />

                        <ds-multiselect
                            v-model="task.assignedUserId"
                            :options="ownersDrop"
                            bind-value-only
                            searchable
                            class="input-border"
                            name="owners"
                            label="Assigned to"
                            placeholder="Assigned to"
                        />

                        <div class="date-fields">
                            <ds-date-picker
                                v-model="task.startDate"
                                class="date-field"
                                name="startDate"
                                label="Start date"
                            />

                            <span class="date-space" />

                            <ds-date-picker
                                v-model="task.completionDate"
                                class="date-field"
                                name="dateCompleted"
                                label="Date completed"
                            />
                        </div>

                        <ds-multiselect
                            v-model="task.taskType"
                            :options="taskTypes"
                            bind-value-only
                            searchable
                            class="input-border"
                            name="taskType"
                            label="Task type"
                            data-qa="task-taskType"
                            placeholder="Task type"
                        />

                        <ds-select-field
                            v-model="task.priority"
                            :options="priorities"
                            bind-value-only
                            class="input-border"
                            name="priority"
                            label="Priority"
                        />

                        <ds-multiselect
                            v-model="task.userIds"
                            bind-value-only
                            class="input-border"
                            searchable
                            multiple
                            placeholder="Notify users"
                            :options="notifyUsersDrop"
                        />

                        <ds-checkbox
                            v-model="task.notifyOwner"
                            class="option-checkbox"
                            name="sendNotification"
                            label="Send individual notifications"
                        />
                    </div>

                    <div>
                        <ds-button
                            id="save-task"
                            :disabled="disableSave"
                            :loading="saving"
                            data-qa="save-task"
                            @click="save"
                        >
                            Save
                        </ds-button>

                        <span class="button-space" />

                        <ds-text-button
                            class="cancel-button"
                            data-qa="cancel-task"
                            @click="modalClose"
                        >
                            Cancel
                        </ds-text-button>

                        <span class="button-space" />

                        <ds-outline-button
                            v-if="task.id > 0"
                            destructive
                            data-qa="delete-task"
                            @click="modalDelete"
                        >
                            Delete
                        </ds-outline-button>
                    </div>
                </div>
            </template>
        </ds-modal>
    </section>
</template>
<script>
import moment from 'moment-timezone';
import axios from 'axios';

import TimeInput from '../shared/TimeInput';

const MINUTE_MILLISECONDS = 60000;

export default {
    components: {
        TimeInput,
    },

    props: {
        contact: Object,
        userId: [String, Number],
    },

    data() {
        return {
            showAction: false,
            actionDisabled: false,
            modalSize: 'med',
            modalTitle: 'Add a Task',
            actionText: 'Mark as complete',
            options: {},
            timeZoneIdServer: '',
            timeZoneIdUser: '',
            task: {
                id: '',
                dueDate: '',
                priority: '',
                taskType: '',
                notes: '',
                title: null,
                remindTimeOffsetInMillis: '',
                startDate: '',
                completionDate: '',
                assignedUserId: 0,
                contactId: 0,
                userIds: [],
                notifyOwner: false,
                dateDiff: '',
            },
            saving: false,
            taskTypes: [],
            dueTime: '',
            priorities: [],
            moreOptions: false,
            multiSelectPlaceholder: 'Notify users',
            submitted: false,
            isTaskModalOpen: false,
        };
    },

    mounted() {
        this.$root.$on('DROPDOWN_LOADED', (dropDownData) => {
            this.options = dropDownData;
            this.initTab();
        });
    },

    beforeDestroy() {
        this.$root.$off('DROPDOWN_LOADED');
    },

    computed: {
        disableSave() {
            return !this.task.title || !this.dueTime;
        },

        defaultTaskEndDate() {
            const date = moment.tz(this.timeZoneIdUser).format('YYYY-MM-DD');

            return moment(date).add(1, 'days').millisecond(0).toDate();
        },

        defaultTaskEndTime() {
            const roundedTime = Math.ceil(moment(moment.tz(this.timeZoneIdUser)).minute() / 15) * 15;

            return moment(moment.tz(this.timeZoneIdUser)).minute(roundedTime).format('hh:mm A');
        },

        fullName() {
            return `${this.contact.firstName} ${this.contact.lastName}`;
        },

        ownersDrop() {
            return this.options.owners;
        },

        notifyUsersDrop() {
            if (!this.ownersDrop || this.ownersDrop.length === 0) {
                return [];
            }

            return this.ownersDrop.slice(1);
        },

        reminderOptionsData() {
            return [
                {
                    label: 'None',
                    value: null,
                }, {
                    label: 'At the time it\'s due',
                    value: 1,
                }, {
                    label: '5 minutes before',
                    value: MINUTE_MILLISECONDS * 5,
                }, {
                    label: '15 minutes before',
                    value: MINUTE_MILLISECONDS * 15,
                }, {
                    label: '30 minutes before',
                    value: MINUTE_MILLISECONDS * 30,
                }, {
                    label: '1 hour before',
                    value: MINUTE_MILLISECONDS * 60,
                }, {
                    label: '2 hours before',
                    value: MINUTE_MILLISECONDS * 60 * 2,
                }, {
                    label: '4 hours before',
                    value: MINUTE_MILLISECONDS * 60 * 4,
                }, {
                    label: '8 hours before',
                    value: MINUTE_MILLISECONDS * 60 * 8,
                }, {
                    label: '1 day before',
                    value: MINUTE_MILLISECONDS * 60 * 24,
                }, {
                    label: '2 days before',
                    value: MINUTE_MILLISECONDS * 60 * 48,
                },
            ];
        },
    },

    methods: {
        showModal(id) {
            if (id > 0) {
                this.modalTitle = 'Edit task';
                this.load(id);
                this.showAction = true;
                this.actionDisabled = false;
            }

            this.task.assignedUserId = Number(this.userId);
            this.task.dueDate = this.defaultTaskEndDate;
            this.task.contactId = Number(this.contact.id);
            this.dueTime = this.defaultTaskEndTime.toString();
            this.initTab();
            this.isTaskModalOpen = true;
            document.querySelector('body').classList.add('overlay-active');
        },

        save() {
            if (this.task.title !== '' && (this.task.id === null || this.task.id === '')) {
                this.saving = true;
                this.task.dueDate = this.updateDateWithDueTime();
                this.task.completionDate = this.getCompletionDate(this.task.completionDate);
                this.task.startDate = this.getCompletionDate(this.task.startDate);
                this.addTask(this.task);
            } else if (this.task.title !== '' && (this.task.id != null && this.task.id > 0)) {
                this.saving = true;
                this.task.dueDate = this.updateDateWithDueTime();
                this.task.completionDate = this.getCompletionDate(this.task.completionDate);
                this.task.startDate = this.getCompletionDate(this.task.startDate);

                if (this.task.userIds === null) {
                    this.task.userIds = [];
                }

                this.updateTask(this.task);
            } else {
                this.submitted = true;
                this.task.title = null;
                this.saving = false;
            }
        },

        async modalDelete() {
            const successMessage = 'Task deleted successfully';
            const errorMessage = 'Unable to delete Task';

            try {
                await axios.delete(`/app/tasks/deleteTask/${this.task.id}`);
                this.$toast({ message: successMessage });

                this.$root.$emit('TASK_STATUS', true);
                this.modalClose();
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        async getAppTimeZones() {
            try {
                const response = await axios.get('/app/tasks/getTimeZones');

                this.timeZoneIdUser = response.data.userTimeZone;
                this.timeZoneIdServer = response.data.serverTimeZone;
            } catch (error) {
                this.$error({ message: 'unable to get user Time Zone' });
            }
        },

        modalClose() {
            this.nestedModal_reset();
            this.isTaskModalOpen = false;
            document.querySelector('body').classList.remove('overlay-active');
        },

        toggleMoreOptions() {
            this.moreOptions = !this.moreOptions;
        },

        updateDateWithDueTime() {
            let momentDate = moment(this.task.dueDate).hours(moment(this.dueTime, 'hh:mm A').get('hour'));

            momentDate = momentDate.minutes(moment(this.dueTime, 'hh:mm A').get('minute'));

            return this.getServerTZDate(momentDate);
        },

        getCompletionDate(date) {
            if (date !== null) {
                const momentDate = moment(date).format('YYYY-MM-DD').toString();

                date = moment.tz(momentDate, this.timeZoneIdServer).toDate();
            }

            return date;
        },

        getServerTZDate(momentStartDate) {
            const strtimewithformat = moment(momentStartDate).format('YYYY-MM-DD HH:mm');

            const strDate = strtimewithformat.concat('');

            const dueTimeStr = this.dueTime;

            if (dueTimeStr === '12:00 AM' || dueTimeStr === '12:15 AM' || dueTimeStr === '12:30 AM' || dueTimeStr === '12:45 AM') {
                const data = strDate.replace('12:', '00:');

                const date = moment.tz(data, this.timeZoneIdServer);

                const formatterdDate = date.format();

                return moment.tz(formatterdDate.concat(''), this.timeZoneIdServer).toDate();
            }
            const date = moment.tz(strDate, this.timeZoneIdServer);

            const formatterdDate = date.format();

            return moment.tz(formatterdDate.concat(''), this.timeZoneIdServer).toDate();
        },

        getDate(date) {
            const momentDate = moment.tz(date, this.timeZoneIdServer).millisecond(0).add(0, 'days').format('YYYY-MM-DD');

            return moment(momentDate).toDate();
        },

        getDateTime() {
            let dueTimeStr = this.dueTime;

            if (dueTimeStr === '12:00 AM' || dueTimeStr === '12:15 AM' || dueTimeStr === '12:30 AM' || dueTimeStr === '12:45 AM') {
                dueTimeStr = dueTimeStr.replace('12', '00');
            }

            return moment(this.task.dueDate).format('MMM/DD/YYYY').toString().concat(' ').concat(dueTimeStr);
        },

        async load(id) {
            try {
                const response = await axios.get(`/app/tasks/loadTaskById/${id}`);

                this.task = response.data;
                this.dueTime = moment.tz(this.task.dueDate, this.timeZoneIdServer).format('hh:mm A').toString();
                this.task.dueDate = moment(moment.tz(this.task.dueDate, this.timeZoneIdServer).format('YYYY-MM-DD')).toDate();

                if (this.task.startDate !== null) {
                    this.task.startDate = this.getDate(this.task.startDate);
                }

                if (this.task.completionDate === null) {
                    this.actionText = 'Mark as complete';
                } else {
                    this.task.completionDate = this.getDate(this.task.completionDate);
                    this.actionText = 'Mark as incomplete';
                }
            } catch (error) {
                this.$error({ message: `Unable to get Task Details by id:${id}` });
            }
        },

        async addTask() {
            const successMessage = 'Task added successfully';
            const errorMessage = 'Unable to Save Task';

            try {
                await axios.post('/app/tasks/addTask', this.task);
                this.$toast({ message: successMessage });
                this.$root.$emit('TASK_STATUS', true);
                this.modalClose();
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        async updateTask() {
            const successMessage = 'Task updated successfully';
            const errorMessage = 'Unable to update Task';

            try {
                await axios.put('/app/tasks/updateTask', this.task);
                this.$toast({ message: successMessage });
                this.$root.$emit('TASK_STATUS', true);
                this.modalClose();
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        async modalAction() {
            let checked;
            const errorMessage = 'Unable to change Task Complete Action';

            if (this.task.completionDate === null) {
                checked = true;
            } else {
                checked = false;
            }

            try {
                await axios.post(`/app/tasks/handleTask?taskId=${this.task.id}&taskComplete=${checked}`);
                this.$root.$emit('TASK_STATUS', true);
                this.modalClose();
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        nestedModal_reset() {
            this.saving = false;
            this.submitted = false;
            this.dueTime = this.defaultTaskEndTime.toString();
            this.task.dueDate = moment().millisecond(0).add(0, 'days').toDate();
            this.task.contactId = Number(this.contact.id);
            this.task.assignedUserId = Number(this.userId);
            this.task.userIds = [];
            this.moreOptions = false;
            this.task.title = null;
            this.task.id = null;
            this.task.completionDate = null;
            this.task.startDate = null;
            this.task.notes = null;
            this.task.priority = '';
            this.task.taskType = '';
            this.task.remindTimeOffsetInMillis = null;
            this.modalTitle = 'Add a Task';
            this.showAction = false;
            this.actionDisabled = true;
        },

        async loadAllAddTaskDropDown() {
            const errorMessage = 'Unable to load Task DropDowns';

            try {
                const response = await axios.get('/app/tasks/loadDropDown');

                this.taskTypes = response.data.taskTypes;

                this.taskTypes = Object.keys(this.taskTypes).map((key) => {
                    return { label: this.taskTypes[key], value: key };
                });

                this.taskTypes.unshift({ label: 'None', value: '' });

                this.priorities = response.data.priorities;
                this.priorities = Object.entries(this.priorities).map((key) => {
                    return { label: key[1], value: Number(key[0]) };
                });
            } catch (error) {
                this.$error({ message: errorMessage });
            }
        },

        initTab() {
            this.loadAllAddTaskDropDown();
            this.getAppTimeZones();
        },
    },
};

</script>

<style lang="scss" scoped>
.date-fields {
    display: flex;
    justify-content: space-between;
}


.date-space {
    margin: $spacing-100 $spacing-200 0 $spacing-200;
    vertical-align: center;
}

.date-field {
    flex: 2;
}

.chevron {
    bottom: -$spacing-050;
    position: relative;
    --icon-size: 1.25rem;
}

.input-border {
    width: 100%;
}

.moreOptions {
    display: flex;
    margin: auto;
    margin-bottom: $spacing-400;
}

.long {
    margin-top: 80rem;
}

@media screen and ( $small ) {
    .input-row {
        display: flex;
        flex-direction: column;
    }
}

.button-space {
    margin-left: $spacing-200;
}
</style>
